import {
    RosterResource,
    TeamResource,
    LeagueResource,
    LeagueQuery,
    IApiTeam,
    IApiRoster,
    IApiRosterHistoryEvent,
    IApiLeague,
    IApiLeagueRestriction,
    IApiTeamMember,
    IApiUser,
    Team,
    GameAccountDescriptionResource,
    IApiGameAccountDescription,
    IApiGameAccount,
    GameAccountResource
} from "ng-league";
import {TeamCacheProxy, UserCacheProxy} from "ng-cache";
import {IFilterService, IQService} from "angular";
import {filter, keyBy, map, maxBy, isUndefined, Dictionary} from "lodash";
import {HumanReadableRosterEventLog, RosterHistoryTransformer} from "../services/RosterHistoryTransformer";

export class PlayRosterPageController {
    // Bindings
    public leagueId: number;
    public teamId: number;
    public dateFormatter: Function;

    // Locals
    public league: IApiLeague;
    public roster: IApiRoster;
    public rosterHistory: IApiRosterHistoryEvent[];
    public readableRosterHistory: HumanReadableRosterEventLog[];
    public team: IApiTeam;
    public teamMembers: {[id: string]: IApiTeamMember} = {};
    public rosterMembers: {[id: string]: IApiUser} = {};
    public gameAccountDescriptions: {[id: string]: IApiGameAccountDescription};
    public error: boolean;
    public gameAccountTypes: string[];
    public gameaccounts: Dictionary<Dictionary<IApiGameAccount>> = {};

    public steamLinkUrlBase = "https://steamcommunity.com/profiles/";

    public static $inject = [
        "$q",
        "$filter",
        "LeagueResource",
        "RosterResource",
        "UserCacheProxy",
        "TeamCacheProxy",
        "TeamResource",
        "GameAccountDescriptionResource",
        "GameAccountResource",
        "RosterHistoryTransformer",
    ];

    constructor(
        public $q: IQService,
        public $filter: IFilterService,
        public LeagueResource: LeagueResource,
        public RosterResource: RosterResource,
        public UserCacheProxy: UserCacheProxy,
        public TeamCacheProxy: TeamCacheProxy,
        public TeamResource: TeamResource,
        public GameAccountDescriptionResource: GameAccountDescriptionResource,
        public GameAccountResource: GameAccountResource,
        public RosterHistoryTransformer: RosterHistoryTransformer
    ) {
    }

    public $onInit() {
        let promise;

        if (!this.gameAccountDescriptions) {
            promise = this.GameAccountDescriptionResource.getGameAccountDescriptions();
        } else {
            promise = this.$q.when(this.gameAccountDescriptions);
        }

        promise.then((descriptions: IApiGameAccountDescription[]) => {
           this.gameAccountDescriptions = keyBy(descriptions, (description: IApiGameAccountDescription) => {
              return description.type;
           });
        }).catch(x => this.error = true);
    }

    public $onChanges(changes: any) {
        if ((changes.leagueId || changes.teamId) && this.leagueId && this.teamId) {
            const leaguePromise = this.LeagueResource.getLeagues({id: this.leagueId} as LeagueQuery)
                .then(leagues => this.league = leagues.pop())
                .catch(x => {
                    this.error = true;
                    return this.$q.reject(x);
                });

            this.TeamResource.getTeamMembers(this.teamId.toString())
                .then(members => this.teamMembers = keyBy(members, (member: IApiTeamMember) => {
                    return member.user.id;
                }));

            this.RosterResource.getRoster(this.leagueId, this.teamId)
                .then(roster => this.roster = roster)
                .catch(x => {
                    this.error = true;
                    return this.$q.reject(x);
                });

            const historyPromise = this.RosterResource.getRosterHistory(this.leagueId, this.teamId)
                .then(rosterHistory => this.rosterHistory = rosterHistory)
                .then(rosterHistory => {
                    const userIds = rosterHistory
                        .filter(event => event.action === "join")
                        .map(event => event.userId.toString());

                    this.UserCacheProxy.getUsers(userIds, user => {
                        this.rosterMembers[user.getId()] = user.getRawData();
                    });

                    return rosterHistory;
                })
                .catch(x => {
                    this.error = true;
                    return this.$q.reject(x);
                });

                historyPromise
                .then(
                    (rosterHistory: IApiRosterHistoryEvent[]) =>
                    this.readableRosterHistory = this.RosterHistoryTransformer.getHistoryLog(rosterHistory)
                )
                .catch(x => this.error = true);

            this.TeamCacheProxy.getTeams([this.teamId.toString()], (team: Team) => {
                this.team = team.getRawData();
            });

            this.$q.all([leaguePromise, historyPromise]).then((data) => {
                const league = data[0] as IApiLeague;
                const rosterHistory = data[1] as IApiRosterHistoryEvent[];

                const userIds = rosterHistory
                    .filter(event => event.action === "join")
                    .map(event => event.userId.toString());

                const restrictions = filter(league.restrictions, (restriction: IApiLeagueRestriction) => {
                    return restriction.type === "gameaccount";
                }) as IApiLeagueRestriction[];

                this.gameAccountTypes = map(restrictions, (restriction: IApiLeagueRestriction) => {
                    return restriction.params.type;
                }) as string[];

                return this.$q.all(userIds.map(userId => {
                    return this.GameAccountResource.getGameAccounts(parseInt(userId, 10)).then(gameaccounts => {
                        this.gameaccounts[userId] = keyBy(gameaccounts, gameaccount => gameaccount.type);
                    });
                }));
            }).catch(x => this.error = true);
        }
    }

    public getSteamLink(gameAccount) {
        return `${this.steamLinkUrlBase}${gameAccount.internalValue}`;
    }

    public getLatestJoinEvent(contestantId: number): IApiRosterHistoryEvent {
        return maxBy(this.rosterHistory, (event: IApiRosterHistoryEvent) => {
            if (event.userId !== contestantId || event.action !== "join") {
                return 0;
            }

            return new Date(event.date).getTime();
        });
    }

    public dateFilter(date: string) {
        if (!isUndefined(this.dateFormatter)) {
            return this.dateFormatter({date: date});
        }

        return this.$filter("date")(date);
    }
}